/**
 * 登录态落库（唯一真相）
 *
 * 登录成功后要把接口返回的身份写进 sessionStorage，这件事在这里做一次，
 * 三处共用：
 *   1. Login.vue        正常登录
 *   2. Account.vue      切换已保存的账号
 *   3. service.js       sid 失效后的自动重登（reLogin）
 *
 * 为什么不能各写一份：历史上 reLogin 就是手抄的第二份，结果漏了 role==6 分支、
 * child 字段名写成 real_name（正确是 name）。抄第三份必然再漂移一次。
 *
 * 只做"写本地 + 算跳转路径"，不发请求、不碰后端。
 */

/** 角色文案：与 User.vue 的角色标签保持一致 */
export const ROLE_TEXT = {
  1: "家长",
  2: "学校老师",
  3: "机构老师",
  6: "巡课老师",
};

/**
 * 登录态相关的 key。清除身份时只删这些，不能整包 sessionStorage.clear()
 * ——页面也会往 sessionStorage 放临时状态（如 AllCourses 选中的周次），
 * 整包清会把它一起冲掉，重登后用户选的周次就丢了。
 */
const LOGIN_KEYS = [
  "userName",
  "userId",
  "userNo",
  "role",
  "childId",
  "childName",
  "className",
  "schoolName",
  "classId",
  "schoolId",
  "area",
  "tSchoolId",
  "tSchoolName",
  "schools",
  "isHeadTeacher",
  "curPwdEnc",
];

/** 只清登录态，保留页面临时状态 */
function clearLoginKeys() {
  LOGIN_KEYS.forEach((k) => sessionStorage.removeItem(k));
}

/** 该角色登录后应该去的页面 */
const ROLE_HOME = {
  1: "/myCourese",
  2: "/teacherHome",
  3: "/teacherHome",
  6: "/dutyInspect",
};

/**
 * 把登录结果写入本地，并返回该去哪
 * @param {object} result  登录接口返回的 data.result
 * @param {string} sid     登录接口返回的 sid（服务端会话，接口都靠它认人）
 * @returns {{ok:boolean, path?:string, role?:number, msg?:string}}
 */
export function applyLoginResult(result, sid) {
  if (!result) return { ok: false, msg: "登录返回为空" };

  // 切换账号时旧身份必须清干净：从家长切到老师，孩子/学校/班主任标记若残留会被新页面读到
  // 但"当前选中的孩子"要留着做匹配（重登时不能把用户选的孩子重置回第一个）
  const prevChildId = sessionStorage.getItem("childId");
  clearLoginKeys();

  if (sid) localStorage.setItem("sid", sid);

  const role = Number(result.type);
  sessionStorage.setItem("userName", result.name || result.user_no || "");
  sessionStorage.setItem("userId", result.user_id);
  sessionStorage.setItem("userNo", result.user_no);
  sessionStorage.setItem("role", role);

  if (role === 1) {
    // 家长
    const childList = result.students || [];
    if (childList.length === 0) {
      return { ok: false, msg: "此账号没有配置学生信息" };
    }
    // 优先沿用上次选中的孩子（重登场景），匹配不到才用第一个（首次登录/切换账号）
    const child =
      childList.find((c) => String(c.id) === String(prevChildId)) || childList[0];
    sessionStorage.setItem("childId", child.id);
    sessionStorage.setItem("childName", child.name);
    sessionStorage.setItem("className", child.class_name);
    sessionStorage.setItem("schoolName", child.school_name);
    sessionStorage.setItem("classId", child.class_id);
    sessionStorage.setItem("schoolId", child.school_id);
    sessionStorage.setItem("area", child.area || ""); // 莱山区传2，其他传1
    return { ok: true, path: ROLE_HOME[1], role };
  }

  if (role === 2) {
    // 学校老师
    sessionStorage.setItem("tSchoolId", result.school_id);
    sessionStorage.setItem("tSchoolName", result.school_name);
    sessionStorage.setItem("isHeadTeacher", (result.classList || []).length > 0);
    return { ok: true, path: ROLE_HOME[2], role };
  }

  if (role === 3) {
    // 机构老师
    const schools = result.schools || [];
    sessionStorage.setItem("schools", JSON.stringify(schools));
    sessionStorage.setItem("tSchoolId", schools[0]?.school_id || "");
    sessionStorage.setItem("tSchoolName", schools[0]?.school_name || "");
    sessionStorage.setItem("isHeadTeacher", false);
    return { ok: true, path: ROLE_HOME[3], role };
  }

  if (role === 6) {
    // 巡课老师：登录后首页直接进巡课管理；不算班主任（2026-08-18 确认口径，与小程序一致）
    sessionStorage.setItem("tSchoolId", result.school_id);
    sessionStorage.setItem("tSchoolName", result.school_name);
    sessionStorage.setItem("isHeadTeacher", false);
    return { ok: true, path: ROLE_HOME[6], role };
  }

  return { ok: false, msg: "未知角色类型" };
}

/**
 * 清除登录态，但保留"已保存账号列表"和微信 openId
 *
 * 为什么不能简单 localStorage.clear()：savedAccounts 是通讯录、openId 是微信身份，
 * 退出登录（或 sid 失效重登失败）不该把它们一起清掉，否则账号切换列表会凭空消失。
 */
export function clearLoginState() {
  const keep = {
    savedAccounts: localStorage.getItem("savedAccounts"),
    openId: localStorage.getItem("openId"),
  };
  localStorage.clear();
  sessionStorage.clear();
  Object.keys(keep).forEach((k) => {
    if (keep[k]) localStorage.setItem(k, keep[k]);
  });
}
